import { AffDashboardVisibilitySettingEnum } from "./affiliate.interface";

export enum SettingIds {
  ENABLED = 'company.enabled',
  APP_TITLE = 'company.appTitle',
  DOMAIN = 'company.domain',
  DIRECT_SETTLE = 'company.directSettlement',
  KYC = 'markets.kycTradeRequirementEnabled',
  GEOBLOCK = 'company.geoblock',
  LOGO = 'appearance.logo',
  THEMES = 'appearance.themes',
  NEW_TAB = 'appearance.newTab',
  TARGET = 'appearance.target',
  REPORT_BUTTONS = 'appearance.reportButtons',
  CUSTOM_MENU_ITEMS = 'appearance.customMenuItems',
  APPEARANCE_CHART_TYPE = 'appearance.chartType',
  APPEARANCE_CHART_INT = 'appearance.chartInt',
  AMM = 'product.amm',
  OBDEX = 'product.obdex',
  POINTS = 'product.pointSystem',
  AFFILIATE_DASHBOARD_THRESHOLD = 'product.affiliateDashboardThreshold',
  AFFILIATE_DEFAULT_FEE_SHARE = 'product.affiliateDefaultFeeShare',
  AFFILIATE_DASHBOARD_VISIBILITY = 'product.affiliateDashboardVisibility',
  AMM_FEE = 'company.ammFee',
  FEE_SHARE = 'company.feeShare',
  MIN_FEE = 'company.minFee',
  MAKER_FEE = 'company.makerFee',
  TAKER_FEE = 'company.takerFee',
  PINNED_PAIRS = "markets.pinnedPairs",
  TWITTER_ENABLED = 'point-system.twitterEnabled',
  TWITTER_JOB_ENABLED = 'point-system.twitterJobEnabled',
  TWITTER_HASHTAGS = 'point-system.twitterHashtags',
  TWITTER_ACCOUNT_NAME = "point-system.twitterAccountName",
  DISCORD_ENABLED = 'point-system.discordEnabled',
  TELEGRAM_ENABLED = 'point-system.telegramEnabled',
  TELEGRAM_GROUP_NAME = 'point-system.telegramGroupName',
  TELEGRAM_BOT_NAME = 'point-system.telegramBotName',
  GUIDE_LINK = 'point-system.guideLink',
}

export interface SettingsInit {
  [SettingIds.LOGO]: string;
  [SettingIds.TARGET]?: string;
  [SettingIds.NEW_TAB]?: string;
  [SettingIds.REPORT_BUTTONS]?: string;
  [SettingIds.CUSTOM_MENU_ITEMS]?: string;
  [SettingIds.THEMES]: string;
  [SettingIds.AMM_FEE]?: string;
  [SettingIds.APP_TITLE]: string;
  [SettingIds.DIRECT_SETTLE]?: string;
  [SettingIds.KYC]?: string;
  [SettingIds.DOMAIN]: string;
  [SettingIds.ENABLED]: string;
  [SettingIds.FEE_SHARE]: string;
  [SettingIds.GEOBLOCK]?: string;
  [SettingIds.MAKER_FEE]?: string;
  [SettingIds.MIN_FEE]?: string;
  [SettingIds.TAKER_FEE]?: string;
  [SettingIds.PINNED_PAIRS]?: string;
  [SettingIds.AMM]: string;
  [SettingIds.OBDEX]: string;
  [SettingIds.POINTS]?: POINTS_SETTING;
  [SettingIds.AFFILIATE_DASHBOARD_THRESHOLD]: string;
  [SettingIds.AFFILIATE_DEFAULT_FEE_SHARE]: string;
  [SettingIds.AFFILIATE_DASHBOARD_VISIBILITY]?: AffDashboardVisibilitySettingEnum;
  [SettingIds.TELEGRAM_ENABLED]?: string;
  [SettingIds.TELEGRAM_GROUP_NAME]?: string;
  [SettingIds.TELEGRAM_BOT_NAME]?: string;
  [SettingIds.DISCORD_ENABLED]?: string;
  [SettingIds.TWITTER_ENABLED]?: string;
  [SettingIds.TWITTER_JOB_ENABLED]?: string;
  [SettingIds.TWITTER_HASHTAGS]?: string;
  [SettingIds.TWITTER_ACCOUNT_NAME]?: string;
  [SettingIds.APPEARANCE_CHART_TYPE]?: string;
  [SettingIds.APPEARANCE_CHART_INT]?: string;
  currentCountry?: string;
  partnerId: number;
  isUltrade: boolean;
  companyId: number;
}

export interface ISettingsState {
  appTitle: string,
  domain: string,
  enabled: string,
  kycTradeRequirementEnabled?: boolean,
  obdex: boolean,
  pointSystem: POINTS_SETTING,
  customMenuItems: CustomMenuItem[],
  affiliateDashboardThreshold: string,
  affiliateDefaultFeeShare: string,
  affiliateDashboardVisibility: AffDashboardVisibilitySettingEnum, 
  feeShare: number,
  ammFee: number,
  makerFee: number,
  minFee: number,
  takerFee: number,
  geoblock: string[],
  pinnedPairs: number[],
  currentCountry: string,
  theme1: ThemeObj,
  theme2: ThemeObj,
  theme3: ThemeObj,
  currentTheme?: string,
  isUltrade: boolean,
  companyId: number,
  target: string,
  newTab: boolean,
  reportButtons: boolean,
  chartType: string,
  chartInt: string[],
}

export interface CustomMenuItem {
  label: string, 
  url: string,
}

export interface ThemeObj {
  logo: string,
  name: string,
  active: boolean,
  value: Theme,
  chartValue: ICHART_VARS
};

export interface CurrentThemeObj {
  currentTheme: string,
  value?: Theme,
  logo?: string
}

export interface Theme {
  "main-background": string;
  "module-background": string;
  "module-border": string;
  "primary-text": string;
  "secondary-text": string;
  "buy-text": string;
  "sell-text": string;
  "buy-background": string;
  "sell-background": string;
  "active-element-default": string;
  "hover-element-default": string;
  "input-background-default": string;
  "input-background-hover": string;
  "input-background-disabled": string;
  "input-border-default": string;
  "input-border-active": string;
  "button-disabled": string;
  "button-border": string;
  "primary-1": string;
  "primary-2": string;
  "pair-active-row": string;
  "dropdown-shadow": string;
  "button-text": string;
};

export interface Themes {
  theme1: {
    name: string,
    active: boolean,
    value: Theme,
    chartValue: ICHART_VARS
  },
  theme2: {
    name: string,
    active: boolean,
    value: Theme,
    chartValue: ICHART_VARS
  },
  theme3: {
    name: string,
    active: boolean,
    value: Theme,
    chartValue: ICHART_VARS
  },
};

export enum MaintenanceMode {
  Off = 0,
  Full,
  Partial,
  Scheduled
}

export enum POINTS_SETTING {
  OFF = "disable",
  ULTRADE = "ultrade",
  ALL ="all"
}

export interface ICHART_VARS {
  "bullish_candle_color": string,
  "bearish_candle_color": string,
  "bullish_wick_color": string,
  "bearish_wick_color": string,
  "bullish_outline_color": string,
  "bearish_outline_color": string,
  "line_color": string,
  "chart_background": string,
  "chart_text": string,
}

export const CHART_TYPE = {
  "Bars": 0,
  "Candles": 1,
  "Line": 2,
  "Area": 3,
  "Heikin-Ashi": 8,
  "Hollow Candles": 9,
  "Baseline": 10,
  "High-Low": 12,
  "Columns": 13,
  "Line with Markers": 14,
  "Step Line": 15,
  "HLC area": 16
} as const;;

export const CHART_INT = {
  "1m": 1,
  "3m": 3,
  "5m": 5,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "2h": 120,
  "3h": 180,
  "1D": "1D",
  "1W": "1W",
  "1M": "1M"
} as const;
